// =====================================================================
// useCrudResource — hook generico que cuida do ciclo CRUD comum
// (list, dialog open/close, create/update, delete confirmation) pra
// evitar repetir o mesmo boilerplate em cada aba.
//
// Fase 3, S38.
//
// Uso:
//   const crud = useCrudResource({
//     slug, list, create, update, remove, resourceLabel: 'profissional'
//   });
//   crud.handleAdd();                // abre dialog em modo novo
//   crud.handleEdit(row);            // abre dialog em modo edit
//   crud.handleDelete(row);          // abre confirm delete
//   crud.handleSave(payload);        // POST ou PUT (decide pelo dialogInitial.id)
//   crud.handleConfirmDelete();      // executa DELETE
//   crud.reload();                   // re-busca a lista
//
// State exposto: rows, loading, saving, dialogOpen, dialogInitial,
// deleteTarget.
// =====================================================================
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

const errorMessage = (err, fallback) =>
  err?.response?.data?.error || err?.message || fallback;

const useCrudResource = ({
  slug,
  list,
  create,
  update,
  remove,
  resourceLabel = "item"
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitial, setDialogInitial] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const reload = useCallback(async () => {
    if (!slug || !list) return;
    try {
      setLoading(true);
      const data = await list(slug);
      // Endpoints retornam formatos variados:
      //  - direto array
      //  - { rows: [...] }
      //  - { data: [...] }
      //  - { success, count, professionals: [...] } (mais comum na Admin v2)
      //  - { success, count, protocols/services/templates/benefits: [...] }
      // Pra robustez, pega a primeira chave que seja array.
      let arr = [];
      if (Array.isArray(data)) {
        arr = data;
      } else if (data && typeof data === "object") {
        if (Array.isArray(data.rows)) arr = data.rows;
        else if (Array.isArray(data.data)) arr = data.data;
        else {
          const arrayKey = Object.keys(data).find((k) => Array.isArray(data[k]));
          if (arrayKey) arr = data[arrayKey];
        }
      }
      setRows(arr);
    } catch (err) {
      toast.error(errorMessage(err, `Erro carregando ${resourceLabel}s`));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [slug, list, resourceLabel]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleAdd = useCallback(() => {
    setDialogInitial(null);
    setDialogOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setDialogInitial(row);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    if (saving) return;
    setDialogOpen(false);
    setDialogInitial(null);
  }, [saving]);

  const handleSave = useCallback(
    async (payload) => {
      if (!slug) return;
      try {
        setSaving(true);
        const id = dialogInitial?.id;
        if (id) {
          if (!update) throw new Error("update fn ausente");
          await update(slug, id, payload);
          toast.success(`${resourceLabel} atualizado`);
        } else {
          if (!create) throw new Error("create fn ausente");
          await create(slug, payload);
          toast.success(`${resourceLabel} criado`);
        }
        setDialogOpen(false);
        setDialogInitial(null);
        await reload();
      } catch (err) {
        toast.error(errorMessage(err, `Erro salvando ${resourceLabel}`));
      } finally {
        setSaving(false);
      }
    },
    [slug, dialogInitial, update, create, reload, resourceLabel]
  );

  const handleDelete = useCallback((row) => {
    setDeleteTarget(row);
  }, []);

  const handleCancelDelete = useCallback(() => {
    if (saving) return;
    setDeleteTarget(null);
  }, [saving]);

  const handleConfirmDelete = useCallback(async () => {
    if (!slug || !deleteTarget || !remove) return;
    try {
      setSaving(true);
      await remove(slug, deleteTarget.id);
      toast.success(`${resourceLabel} excluído`);
      setDeleteTarget(null);
      await reload();
    } catch (err) {
      toast.error(errorMessage(err, `Erro excluindo ${resourceLabel}`));
    } finally {
      setSaving(false);
    }
  }, [slug, deleteTarget, remove, reload, resourceLabel]);

  return {
    rows,
    loading,
    saving,
    dialogOpen,
    dialogInitial,
    deleteTarget,
    handleAdd,
    handleEdit,
    handleDelete,
    handleSave,
    handleCloseDialog,
    handleConfirmDelete,
    handleCancelDelete,
    reload
  };
};

export default useCrudResource;
